/**
 * The "celebration moment" the M8 acceptance criteria call for —
 * a small, restrained overlay that surfaces a decided opportunity
 * without leaning into gamification anti-patterns (which the RD doc
 * explicitly warns against). A single chip pulses up from the bottom
 * of the viewport, names the opportunity, fades, and disappears.
 * No confetti, no sound, no streak counter.
 *
 * UAT 7.7 through 7.9 lifted the trigger into the shell-level
 * `useCelebration` store. The component reads the queued name from
 * the store, animates in, and self-dismisses after 2.4 seconds. The
 * shell (`__root.tsx`) mounts a single instance so the overlay fires
 * regardless of which surface raised the outcome — board drag or
 * slide-over button. LOST renders the same chip with a neutral grey
 * framing so the user still gets visual confirmation without the
 * surface implying a win.
 *
 * Reference: spec section 15.8, design README section 5.7, rd-doc.
 */

import { useEffect, useState } from 'react';

import { useCelebration } from '@/lib/celebration';
import { cn } from '@/lib/utils';

/**
 * Auto-dismiss delay in milliseconds. The chip's enter transition runs
 * for 500ms; 2.4s gives the eye roughly two seconds of legibility
 * before the fade-out begins — enough to register the outcome without
 * disrupting the next interaction.
 */
const AUTO_DISMISS_MS = 2400;

export function CelebrationMoment() {
  const opportunityName = useCelebration((s) => s.opportunityName);
  const outcome = useCelebration((s) => s.outcome);
  const dismiss = useCelebration((s) => s.dismiss);

  // Triggers the CSS transition by flipping `visible` after mount so
  // the chip fades up from below rather than appearing in place.
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!opportunityName) {
      // Reset the visible flag when the store clears so the next show
      // call replays the enter animation from the off-screen state.
      setVisible(false);
      return;
    }

    const enter = requestAnimationFrame(() => setVisible(true));
    const exit = setTimeout(() => dismiss(), AUTO_DISMISS_MS);
    return () => {
      cancelAnimationFrame(enter);
      clearTimeout(exit);
    };
  }, [opportunityName, dismiss]);

  if (!opportunityName || !outcome) return null;

  // LOST uses the neutral grey-800 surface so the chip reads as a
  // quiet acknowledgement rather than a celebration. WON keeps the
  // mx-pos green surface per the design README.
  const isLost = outcome === 'LOST';

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="celebration-moment"
      data-outcome={outcome}
      className={cn(
        'pointer-events-none fixed bottom-mx-6 left-1/2 z-50 -translate-x-1/2 transition-all duration-500 ease-mx-default',
        visible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0',
      )}
    >
      <div
        className={cn(
          'flex items-center gap-mx-3 border px-mx-4 py-mx-3 text-mx-white',
          isLost ? 'border-mx-grey-700 bg-mx-grey-800' : 'border-mx-pos bg-mx-pos',
        )}
      >
        <span className="mono-label text-mx-mono-sm">{outcome}</span>
        <span className="text-mx-body-sm font-medium">{opportunityName}</span>
      </div>
    </div>
  );
}
